import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Smartphone, 
  Shield, 
  Upload, 
  Users, 
  Download, 
  Eye, 
  Clock, 
  Heart,
  Lock,
  Zap,
  Star,
  CheckCircle
} from 'lucide-react';

const CreatorFeatures = () => {
  const features = [
    {
      icon: Smartphone,
      title: "Mobile-First Upload",
      description: "Upload directly from your phone's camera or photo library. No complicated setup or transfers needed.",
      highlight: "Instant sharing from sidelines"
    },
    {
      icon: Shield,
      title: "Private & Secure",
      description: "Your videos are private by default. Only people you invite can see your family's content.",
      highlight: "No ads, no algorithms"
    },
    {
      icon: Users,
      title: "Family Sharing",
      description: "Invite grandparents, relatives, and close friends. Everyone stays connected to your family's journey.",
      highlight: "Never miss a moment"
    },
    {
      icon: Download,
      title: "Download & Keep",
      description: "Download your videos anytime. Even if you cancel, your memories stay with you.",
      highlight: "Your memories, forever"
    },
    {
      icon: Eye,
      title: "Privacy Controls",
      description: "Choose exactly who can see each video. Public, family-only, or specific people - you decide.",
      highlight: "Total control"
    },
    {
      icon: Clock,
      title: "Organized Timeline",
      description: "Videos automatically organize by date and family member. Find any moment instantly.",
      highlight: "Never lose a memory"
    }
  ];

  const comparison = [
    {
      feature: "Safe for Kids",
      hiveshare: true,
      youtube: false,
      social: false
    },
    {
      feature: "No Ads",
      hiveshare: true,
      youtube: false,
      social: false
    },
    {
      feature: "Private Sharing",
      hiveshare: true,
      youtube: false,
      social: "limited"
    },
    {
      feature: "Mobile Upload",
      hiveshare: true,
      youtube: true,
      social: true
    },
    {
      feature: "Family Focused",
      hiveshare: true,
      youtube: false,
      social: false
    },
    {
      feature: "Unlimited Storage",
      hiveshare: true,
      youtube: true,
      social: "limited"
    }
  ];

  const renderCheckmark = (value: boolean | string) => {
    if (value === true) {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (value === false) {
      return <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
        <div className="h-2 w-2 bg-red-500 rounded-full"></div>
      </div>;
    } else {
      return <div className="h-5 w-5 rounded-full bg-yellow-100 flex items-center justify-center">
        <div className="h-2 w-2 bg-yellow-500 rounded-full"></div>
      </div>;
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Everything You Need to Share Safely
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple features designed specifically for families and individual creators who want to share their journey without compromising privacy or safety.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-gray-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center mb-3">
                    <div className="p-3 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 mr-4">
                      <feature.icon className="h-6 w-6 text-purple-600" />
                    </div>
                    <CardTitle className="text-lg font-bold text-gray-900">
                      {feature.title}
                    </CardTitle>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-3 py-1 rounded-full inline-block">
                    <span className="text-sm font-medium text-purple-700">
                      {feature.highlight}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why Not Just Use YouTube or Social Media?
            </h3>
            <p className="text-lg text-gray-600">
              Because your family deserves better than ads, algorithms, and strangers seeing your kids.
            </p>
          </div>

          <Card className="overflow-hidden border-2 border-purple-200">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6">
              <div className="grid grid-cols-4 gap-4 font-semibold text-gray-800">
                <div>Feature</div>
                <div className="text-center">HiveShare</div>
                <div className="text-center text-gray-600">YouTube</div>
                <div className="text-center text-gray-600">Social Media</div>
              </div>
            </div>
            
            <CardContent className="p-0">
              {comparison.map((item, index) => (
                <div key={index} className={`grid grid-cols-4 gap-4 p-4 items-center ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <div className="font-medium text-gray-800">{item.feature}</div>
                  <div className="text-center">{renderCheckmark(item.hiveshare)}</div>
                  <div className="text-center">{renderCheckmark(item.youtube)}</div>
                  <div className="text-center">{renderCheckmark(item.social)}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6 text-center">
                <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                  <div className="h-3 w-3 bg-red-500 rounded-full"></div>
                </div>
                <h4 className="font-bold text-red-800 mb-2">YouTube/Social</h4>
                <p className="text-sm text-red-700">Ads, strangers, algorithms controlling what people see</p>
              </CardContent>
            </Card>

            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-6 text-center">
                <div className="h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
                  <div className="h-3 w-3 bg-yellow-500 rounded-full"></div>
                </div>
                <h4 className="font-bold text-yellow-800 mb-2">Other Platforms</h4>
                <p className="text-sm text-yellow-700">Limited privacy, not designed for families</p>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <h4 className="font-bold text-green-800 mb-2">HiveShare Creator</h4>
                <p className="text-sm text-green-700">Private, safe, built specifically for families</p>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorFeatures;